import type { AgencyListTab } from '@saaga/shared/interfaces/agencies.interface'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSubscriptionState } from '@/hooks/billing/use-subscription-state.hook'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { useOrganizationEarlyAccess } from '@/hooks/use-organization-early-access.hook'

import { isProOnlyTab, PRO_ONLY_TAB_LIST } from './agency-tab-access.util'

const EMPTY_TAB_LIST: AgencyListTab[] = []

interface UseAgencyProAccessReturn {
  disabledTabList: AgencyListTab[]
  handleUpgradeClick: () => void
}

/**
 * Handles Pro subscription gating for agency tabs.
 * Redirects non-Pro users away from pro-only tabs and provides the list
 * of tabs that should be disabled in the UI.
 *
 * Early Access tenants sit on the Free plan in billing but are granted
 * Pro-level capabilities, so they are treated as Pro here — they can
 * browse every tab and never see the upgrade CTA.
 */
export const useAgencyProAccess = (activeTab: AgencyListTab): UseAgencyProAccessReturn => {
  const { isFreePlan } = useSubscriptionState()
  const { isEarlyAccess } = useOrganizationEarlyAccess()
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()

  const isLockedOut = isFreePlan && !isEarlyAccess

  useEffect(() => {
    if (isLockedOut && isProOnlyTab(activeTab)) {
      navigate(`/${orgSlug}/agencies/explore`, { replace: true })
    }
  }, [isLockedOut, activeTab, navigate, orgSlug])

  const handleUpgradeClick = useCallback((): void => {
    navigate(`/${orgSlug}/settings/subscription`)
  }, [navigate, orgSlug])

  return {
    disabledTabList: isLockedOut ? PRO_ONLY_TAB_LIST : EMPTY_TAB_LIST,
    handleUpgradeClick,
  }
}
